import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, Timestamp, doc, getDoc } from 'firebase/firestore';
import { functions, db } from './firebase';
import {
  toLATime,
  nowInLA,
  getDayOfWeekLA,
  formatTimeLA,
  getStartOfDayLA,
  getEndOfDayLA,
  addDaysLA,
  parseTimeStringLA,
  APP_TIMEZONE
} from '../utils/timezone';
import { formatSlotId } from '../types/availability';

export interface TimeSlot {
  time: string;
  available: boolean;
  appointmentId?: string;
  reason?: 'booked' | 'admin' | string; // Reason why slot is unavailable
  disabledBy?: string; // Who disabled it (admin, system, etc.)
}

export interface AvailabilityRequest {
  date: string; // YYYY-MM-DD format
  timezone: string;
}

export interface AvailabilityResponse {
  success: boolean;
  date: string;
  timeSlots: TimeSlot[];
  error?: string;
}

class AvailabilityService {
  // Default business hours and slot configuration
  private readonly DEFAULT_BUSINESS_HOURS = {
    weekdays: {
      start: '09:00',
      end: '15:30',
      lunchBreak: { start: '12:00', end: '13:00' }
    },
    weekends: {
      start: '09:00',
      end: '15:30',
      lunchBreak: { start: '12:00', end: '13:00' }
    }
  };

  private readonly SLOT_DURATION = 30; // 30 minutes per slot
  private readonly APPOINTMENT_DURATION = 15; // 15 minutes appointment + 15 minutes buffer

  /**
   * Get available time slots for a specific date (in LA timezone)
   * Now simplified to only check disabled slots since booked appointments are automatically added there
   */
  async getAvailableTimeSlots(date: Date): Promise<TimeSlot[]> {
    try {
      // Get all possible time slots for the date
      const allSlots = this.generateTimeSlots(date);
      
      // Get disabled slots for this date (includes both admin-disabled and booked slots)
      const dateStr = this.formatDateString(date);
      const disabledSlotsData = await this.getDisabledSlotsWithDetails(dateStr);
      
      // Mark slots as unavailable if they are disabled (which includes booked appointments)
      const availableSlots = allSlots.map(slot => {
        const disabledSlot = disabledSlotsData.find(ds => ds.time === slot.time);
        const isDisabled = !!disabledSlot;
        const inBusinessHours = this.isSlotInBusinessHours(slot.time, date);
        
        return {
          ...slot,
          available: !isDisabled && inBusinessHours,
          appointmentId: disabledSlot?.appointmentId,
          reason: disabledSlot?.reason,
          disabledBy: disabledSlot?.disabledBy,
        };
      });

      return availableSlots;
    } catch (error) {
      console.error('Error getting available time slots:', error);
      // Return basic slots if there's an error
      return this.generateTimeSlots(date);
    }
  }

  /**
   * Check if a specific time slot is available
   * Simplified to only check disabled slots since booked appointments are automatically added there
   */
  async isTimeSlotAvailable(date: Date, time: string): Promise<boolean> {
    try {
      // Check if slot is within business hours
      if (!this.isSlotInBusinessHours(time, date)) {
        return false;
      }

      // Check disabled slots (includes both admin-disabled and booked appointments)
      const dateStr = this.formatDateString(date);
      const disabledSlots = await this.getDisabledSlots(dateStr);
      const isDisabled = disabledSlots.has(time);

      return !isDisabled;
    } catch (error) {
      console.error('Error checking time slot availability:', error);
      return false;
    }
  }

  /**
   * Generate all possible time slots for a date (in LA timezone)
   */
  private generateTimeSlots(date: Date): TimeSlot[] {
    const dayOfWeek = getDayOfWeekLA(date);
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    const businessHours = isWeekend 
      ? this.DEFAULT_BUSINESS_HOURS.weekends 
      : this.DEFAULT_BUSINESS_HOURS.weekdays;

    const slots: TimeSlot[] = [];
    
    // Parse start and end times
    const [startHour, startMin] = businessHours.start.split(':').map(Number);
    const [endHour, endMin] = businessHours.end.split(':').map(Number);
    
    // Generate slots every 30 minutes
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      const timeStr = this.formatTimeSlot(currentHour, currentMin);
      
      // Skip lunch break if applicable
      if (businessHours.lunchBreak) {
        const [lunchStartHour, lunchStartMin] = businessHours.lunchBreak.start.split(':').map(Number);
        const [lunchEndHour, lunchEndMin] = businessHours.lunchBreak.end.split(':').map(Number);
        
        const currentMinutes = currentHour * 60 + currentMin;
        const lunchStartMinutes = lunchStartHour * 60 + lunchStartMin;
        const lunchEndMinutes = lunchEndHour * 60 + lunchEndMin;
        
        if (currentMinutes >= lunchStartMinutes && currentMinutes < lunchEndMinutes) {
          // Skip lunch break slots
          currentMin += this.SLOT_DURATION;
          if (currentMin >= 60) {
            currentHour += Math.floor(currentMin / 60);
            currentMin = currentMin % 60;
          }
          continue;
        }
      }
      
      slots.push({
        time: timeStr,
        available: true, // Will be updated based on existing appointments
      });
      
      // Move to next slot
      currentMin += this.SLOT_DURATION;
      if (currentMin >= 60) {
        currentHour += Math.floor(currentMin / 60);
        currentMin = currentMin % 60;
      }
    }
    
    return slots;
  }


  /**
   * Check if a time slot is within business hours (in LA timezone)
   */
  private isSlotInBusinessHours(timeStr: string, date: Date): boolean {
    const dayOfWeek = getDayOfWeekLA(date);
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    const businessHours = isWeekend 
      ? this.DEFAULT_BUSINESS_HOURS.weekends 
      : this.DEFAULT_BUSINESS_HOURS.weekdays;

    // Convert time string to minutes for comparison
    const timeMinutes = this.timeStringToMinutes(timeStr);
    const startMinutes = this.timeStringToMinutes(businessHours.start + ':00');
    const endMinutes = this.timeStringToMinutes(businessHours.end + ':00');
    
    let inBusinessHours = timeMinutes >= startMinutes && timeMinutes < endMinutes;
    
    // Check lunch break
    if (businessHours.lunchBreak && inBusinessHours) {
      const lunchStartMinutes = this.timeStringToMinutes(businessHours.lunchBreak.start + ':00');
      const lunchEndMinutes = this.timeStringToMinutes(businessHours.lunchBreak.end + ':00');
      
      if (timeMinutes >= lunchStartMinutes && timeMinutes < lunchEndMinutes) {
        inBusinessHours = false;
      }
    }
    
    return inBusinessHours;
  }

  /**
   * Format time slot as display string (e.g., "9:00 AM")
   */
  private formatTimeSlot(hour: number, minute: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const displayMinute = minute.toString().padStart(2, '0');
    
    return `${displayHour}:${displayMinute} ${period}`;
  }

  /**
   * Format Date object to time string (in LA timezone)
   */
  private formatTime(date: Date): string {
    return formatTimeLA(date);
  }

  /**
   * Convert time string to minutes since midnight
   */
  private timeStringToMinutes(timeStr: string): number {
    const [time, period] = timeStr.split(' ');
    const [hourStr, minuteStr] = time.split(':');
    let hour = parseInt(hourStr, 10);
    const minute = parseInt(minuteStr || '0', 10);
    
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    
    return hour * 60 + minute;
  }

  /**
   * Get disabled slots for a specific date
   */
  private async getDisabledSlots(dateStr: string): Promise<Set<string>> {
    try {
      const disabledSlotsRef = collection(db, 'disabledSlots');
      const q = query(disabledSlotsRef, where('date', '==', dateStr));
      
      const querySnapshot = await getDocs(q);
      const disabledTimes = new Set<string>();
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        disabledTimes.add(data.time);
      });
      
      return disabledTimes;
    } catch (error) {
      console.error('Error fetching disabled slots:', error);
      return new Set();
    }
  }

  /**
   * Get disabled slots with detailed information for admin view
   */
  private async getDisabledSlotsWithDetails(dateStr: string): Promise<Array<{time: string, appointmentId?: string, reason?: string, disabledBy?: string}>> {
    try {
      const disabledSlotsRef = collection(db, 'disabledSlots');
      const q = query(disabledSlotsRef, where('date', '==', dateStr));
      
      const querySnapshot = await getDocs(q);
      const disabledSlots: Array<{time: string, appointmentId?: string, reason?: string, disabledBy?: string}> = [];
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        disabledSlots.push({
          time: data.time,
          appointmentId: data.appointmentId,
          reason: data.reason,
          disabledBy: data.disabledBy || 'admin'
        });
      });
      
      return disabledSlots;
    } catch (error) {
      console.error('Error fetching disabled slots with details:', error);
      return [];
    }
  }

  /**
   * Format date to YYYY-MM-DD string
   */
  private formatDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get available dates (next 30 days excluding past dates) in LA timezone
   */
  getAvailableDates(): Date[] {
    const dates: Date[] = [];
    const today = nowInLA();
    
    // Start from tomorrow
    for (let i = 1; i <= 30; i++) {
      const date = addDaysLA(today, i);
      dates.push(date);
    }
    
    return dates;
  }

  /**
   * Check if a date is available for booking (not in the past, within 30 days) in LA timezone
   */
  isDateAvailable(date: Date): boolean {
    const today = getStartOfDayLA(nowInLA());
    const checkDate = getStartOfDayLA(date);
    
    const diffTime = checkDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 1 && diffDays <= 30;
  }
}

// Export singleton instance
export const availabilityService = new AvailabilityService();
export default availabilityService;
